var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useMemo, useEffect } from 'react';
import { convertToKAndM, LoaderComp, DashboardStats, SearchInputV1, FilterTabV1, NotificationComp, RowV1, RowHeaderV1, } from 'ui-kit';
import { getIcon } from '../utils/getIcon';
import { getImage } from '../utils/getImage';
import { handleSortTransition, sortPools } from '../utils/sortUtils';
import { getDecimalPlaces, parseCurrencyString } from '../utils/currencyUtils';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useEventSubscription, useSubscription } from '../hooks';
import { SwapModal } from '../components';
import { SwapButton } from '../components/SwapButton';
import { useSignAndExecuteTransaction } from '@mysten/dapp-kit';
import { useNetworkString } from '../context/NetworkContext';
import { useBalances } from '../context/BalanceContext';
import { transactionHandler } from '../utils/transactionHandlers';
import { createAlphaFiSDK } from '../utils/networkUtils';
import { getButtonState } from '../utils/buttonStateUtils';
const AVAILABLE_TABS = ['All', 'ALPHA', 'SUI', 'stSUI', 'Stables', 'Holdings', 'Retired'];
const PoolsPage = ({ walletConnectFunctionality, isWalletConnected, themeName, isMobile, address, }) => {
    var _a;
    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();
    const getInitialTab = () => {
        const tabParam = searchParams.get('tab');
        if (tabParam) {
            const matchedTab = AVAILABLE_TABS.find((tab) => tab.toLowerCase() === tabParam.toLowerCase());
            return matchedTab || 'All';
        }
        return 'All';
    };
    const [activeTab, setActiveTab] = useState(getInitialTab());
    const [searchTerm, setSearchTerm] = useState('');
    const [poolTabStates, setPoolTabStates] = useState({});
    const [poolExpansionStates, setPoolExpansionStates] = useState({});
    const [poolInputValues, setPoolInputValues] = useState({});
    const [poolMaxStates, setPoolMaxStates] = useState({});
    const [notificationInfo, setNotificationInfo] = useState({
        title: '',
        description: '',
        spinner: false,
        link: '',
        image: undefined,
    });
    const [isNotificationOpen, setIsNotificationOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [isSwapModalOpen, setIsSwapModalOpen] = useState(false);
    const [isZapInActive, setIsZapInActive] = useState(false);
    const [zapInQuoteDetails, setZapInQuoteDetails] = useState({
        zapInLoader: true,
        zapInQuoteLoader: false,
        poolDetailsPage: false,
    });
    const [slippage, setSlippage] = useState(1);
    const network = useNetworkString();
    const { walletBalancesMap } = useBalances();
    const { mutate: signAndExecuteTransaction } = useSignAndExecuteTransaction();
    useEffect(() => {
        const tabParam = searchParams.get('tab');
        if (tabParam) {
            const matchedTab = AVAILABLE_TABS.find((tab) => tab.toLowerCase() === tabParam.toLowerCase());
            if (matchedTab) {
                setActiveTab(matchedTab);
            }
        }
        else {
            setActiveTab('All');
        }
    }, [searchParams]);
    useEffect(() => {
        const updateZapInputBoxIcons = () => {
            setZapInQuoteDetails((prev) => (Object.assign(Object.assign({}, prev), { reverseIcon: themeName === 'dark'
                    ? getIcon('reverseTokensIconDarkTheme')
                    : getIcon('reverseTokensIcon'), slippageIcon: themeName === 'dark' ? getIcon('whiteSlippageIcon') : getIcon('zapSlippageIcon'), zapInIcon: themeName === 'dark' && isZapInActive
                    ? getIcon('zapInIcon')
                    : themeName === 'dark' && !isZapInActive
                        ? getIcon('zapInIconDarkTheme')
                        : themeName !== 'dark' && isZapInActive
                            ? getIcon('zapInIconDarkTheme')
                            : themeName !== 'dark' && !isZapInActive
                                ? getIcon('zapInIcon')
                                : '', depositsIcon: themeName === 'dark' ? getIcon('depositsIconForDarkTheme') : getIcon('depositsIcon'), poolDetailsPage: false })));
        };
        if (isZapInActive) {
            updateZapInputBoxIcons();
        }
    }, [themeName, isZapInActive]);
    const { data: staticData, isLoading: isSystemLoading } = useEventSubscription('system', 'static_data');
    const { data: protocolData } = useSubscription('protocol');
    const { data: Portfolio } = useSubscription(address ? `portfolio:${address}` : null);
    const { data: coinInfoData } = useSubscription('coin_info');
    const [sortConfig, setSortConfig] = useState(null);
    const calculateTokenRatios = (poolId, inputTokenSymbol, inputValue, poolTokens) => {
        var _a, _b, _c;
        const amount = parseFloat(inputValue);
        if (!inputValue.trim() || isNaN(amount) || amount === 0) {
            return Object.fromEntries(poolTokens.map((t) => [t.symbol, '']));
        }
        if (poolTokens.length === 1) {
            return { [inputTokenSymbol]: inputValue };
        }
        const immediateResult = Object.fromEntries(poolTokens.map((t) => [t.symbol, t.symbol === inputTokenSymbol ? inputValue : '']));
        try {
            const poolStatic = staticData === null || staticData === void 0 ? void 0 : staticData.find((pool) => pool.pool_id === poolId);
            const inputAssetType = (_b = (_a = poolStatic === null || poolStatic === void 0 ? void 0 : poolStatic.asset_types) === null || _a === void 0 ? void 0 : _a.find((a) => a.asset_symbol === inputTokenSymbol)) === null || _b === void 0 ? void 0 : _b.asset_type;
            const inputDecimals = Number(((_c = coinInfoData === null || coinInfoData === void 0 ? void 0 : coinInfoData[inputAssetType || '']) === null || _c === void 0 ? void 0 : _c.decimals) || 0);
            const amountInSmallestUnit = (amount * Math.pow(10, inputDecimals)).toString();
            const alphafiSdk = createAlphaFiSDK(network, address || '');
            const isAmountA = poolTokens[0].symbol === inputTokenSymbol;
            alphafiSdk
                .estimateLpAmounts({ poolId, amount: amountInSmallestUnit, isAmountA })
                .then(([amount1, amount2]) => {
                var _a, _b, _c;
                const tokenASymbol = poolTokens[0].symbol;
                const otherToken = poolTokens.find((t) => t.symbol !== inputTokenSymbol);
                if (!otherToken)
                    return;
                const otherAssetType = (_b = (_a = poolStatic === null || poolStatic === void 0 ? void 0 : poolStatic.asset_types) === null || _a === void 0 ? void 0 : _a.find((a) => a.asset_symbol === otherToken.symbol)) === null || _b === void 0 ? void 0 : _b.asset_type;
                const otherDecimals = Number(((_c = coinInfoData === null || coinInfoData === void 0 ? void 0 : coinInfoData[otherAssetType || '']) === null || _c === void 0 ? void 0 : _c.decimals) || 0);
                const smallestUnit = otherToken.symbol === tokenASymbol ? amount1 : amount2;
                const otherValueRaw = parseFloat(smallestUnit) / Math.pow(10, otherDecimals);
                const otherValue = otherValueRaw < 1
                    ? parseFloat(otherValueRaw.toFixed(8)).toString()
                    : parseFloat(otherValueRaw.toFixed(6)).toString();
                setPoolInputValues((prev) => (Object.assign(Object.assign({}, prev), { [poolId]: Object.assign(Object.assign({}, prev[poolId]), { [inputTokenSymbol]: inputValue, [otherToken.symbol]: otherValue }) })));
            })
                .catch((err) => console.error('🔍 SDK Error calculating token ratios:', err));
        }
        catch (err) {
            console.error('⚠️ Error in calculateTokenRatios:', err);
        }
        return immediateResult;
    };
    const handleSwapNotification = (info) => {
        setNotificationInfo({
            title: info.title,
            description: info.description,
            spinner: info.spinner || false,
            link: info.link || '',
            image: info.image || undefined,
        });
        setIsNotificationOpen(true);
    };
    const suiBalance = (walletBalancesMap === null || walletBalancesMap === void 0 ? void 0 : walletBalancesMap.get('0x2::sui::SUI')) || '0';
    const handleDeposit = (poolId, amounts) => __awaiter(void 0, void 0, void 0, function* () {
        var _a, _b, _c, _d;
        if (!address) {
            console.error('No wallet connected');
            return;
        }
        const pool = pageData.pools.find((p) => p.pool.id === poolId);
        const formattedAmounts = Object.entries(amounts)
            .map(([coin, amount]) => `${Number(amount).toFixed(getDecimalPlaces(coin))} ${coin}`)
            .join(' and ');
        const tokenSymbols = Object.keys(amounts);
        const isAmountA = tokenSymbols.length > 1 ? tokenSymbols.indexOf(Object.keys(amounts)[0]) === 0 : undefined;
        const amount = Object.values(amounts)[0];
        const firstTokenSymbol = tokenSymbols[0];
        const poolStatic = staticData === null || staticData === void 0 ? void 0 : staticData.find((pool) => pool.pool_id === poolId);
        const firstAssetType = (_b = (_a = poolStatic === null || poolStatic === void 0 ? void 0 : poolStatic.asset_types) === null || _a === void 0 ? void 0 : _a.find((asset) => asset.asset_symbol === firstTokenSymbol)) === null || _b === void 0 ? void 0 : _b.asset_type;
        try {
            yield transactionHandler({
                poolName: (_c = pageData.pools.find((p) => p.pool.id === poolId)) === null || _c === void 0 ? void 0 : _c.pool.name,
                amount: amount,
                poolId: poolId,
                decimals: (firstAssetType && ((_d = coinInfoData === null || coinInfoData === void 0 ? void 0 : coinInfoData[firstAssetType]) === null || _d === void 0 ? void 0 : _d.decimals)) || '',
                network: network,
                action: 'deposit',
                modalState: {
                    isOpen: true,
                    token: (pool === null || pool === void 0 ? void 0 : pool.tokens[0]) || null,
                    actionType: 'deposit',
                },
                address: address,
                signAndExecuteTransaction: signAndExecuteTransaction,
                setNotificationInfo: setNotificationInfo,
                setIsNotificationOpen: setIsNotificationOpen,
                setIsTransactionLoading: setLoading,
                displayAmount: formattedAmounts,
                suiBalance: suiBalance,
                isAmountA: isAmountA,
                resetInputValues: () => {
                    setPoolInputValues((prev) => (Object.assign(Object.assign({}, prev), { [poolId]: Object.fromEntries((pool === null || pool === void 0 ? void 0 : pool.tokens.map((t) => [t.symbol, ''])) || []) })));
                },
            });
        }
        catch (error) {
            console.error('Deposit failed:', error);
        }
    });
    const handleZapInAndDeposit = (poolId) => __awaiter(void 0, void 0, void 0, function* () {
        var _a, _b, _c, _d;
        if (!address) {
            console.error('No wallet connected');
            return;
        }
        const pool = pageData.pools.find((p) => p.pool.id === poolId);
        const formattedAmounts = `${zapInQuoteDetails === null || zapInQuoteDetails === void 0 ? void 0 : zapInQuoteDetails.token1Amount} ${zapInQuoteDetails === null || zapInQuoteDetails === void 0 ? void 0 : zapInQuoteDetails.token1Symbol} and ${zapInQuoteDetails === null || zapInQuoteDetails === void 0 ? void 0 : zapInQuoteDetails.token2Amount} ${zapInQuoteDetails === null || zapInQuoteDetails === void 0 ? void 0 : zapInQuoteDetails.token2Symbol}`;
        // const firstTokenSymbol = tokenSymbols[0];
        const poolStatic = staticData === null || staticData === void 0 ? void 0 : staticData.find((pool) => pool.pool_id === poolId);
        const activeAsset = (_b = (_a = poolStatic === null || poolStatic === void 0 ? void 0 : poolStatic.asset_types) === null || _a === void 0 ? void 0 : _a.find((asset) => asset.asset_symbol === (zapInQuoteDetails === null || zapInQuoteDetails === void 0 ? void 0 : zapInQuoteDetails.activeToken))) === null || _b === void 0 ? void 0 : _b.asset_type;
        try {
            yield transactionHandler({
                poolName: (_c = pageData.pools.find((p) => p.pool.id === poolId)) === null || _c === void 0 ? void 0 : _c.pool.name,
                amount: (zapInQuoteDetails === null || zapInQuoteDetails === void 0 ? void 0 : zapInQuoteDetails.depositAmountWithoutExponent) || '0',
                poolId: poolId,
                decimals: (activeAsset && ((_d = coinInfoData === null || coinInfoData === void 0 ? void 0 : coinInfoData[activeAsset]) === null || _d === void 0 ? void 0 : _d.decimals)) || '',
                network: network,
                action: 'zap_deposit',
                modalState: {
                    isOpen: true,
                    token: (pool === null || pool === void 0 ? void 0 : pool.tokens[0]) || null,
                    actionType: 'zap_deposit',
                },
                address: address,
                signAndExecuteTransaction: signAndExecuteTransaction,
                setNotificationInfo: setNotificationInfo,
                setIsNotificationOpen: setIsNotificationOpen,
                setIsTransactionLoading: setLoading,
                displayAmount: formattedAmounts,
                suiBalance: suiBalance,
                isAmountA: zapInQuoteDetails === null || zapInQuoteDetails === void 0 ? void 0 : zapInQuoteDetails.isInputA,
                slippage: slippage,
                resetInputValues: () => {
                    setPoolInputValues((prev) => (Object.assign(Object.assign({}, prev), { [poolId]: Object.fromEntries((pool === null || pool === void 0 ? void 0 : pool.tokens.map((t) => [t.symbol, ''])) || []) })));
                    setZapInQuoteDetails((prev) => {
                        var _a;
                        return (Object.assign(Object.assign({}, prev), { zapInLoader: true, zapInQuoteLoader: false, poolId: ((_a = pageData.pools.find((p) => p.pool.id === poolId)) === null || _a === void 0 ? void 0 : _a.pool.strategyType) ===
                                'Leveraged Yield Farming'
                                ? ''
                                : poolId, poolDetailsPage: false, zapSlippage: '' }));
                    });
                },
            });
        }
        catch (error) {
            console.error('Deposit failed:', error);
        }
    });
    const handleWithdraw = (poolId, amounts) => __awaiter(void 0, void 0, void 0, function* () {
        var _a, _b, _c, _d;
        if (!address) {
            console.error('No wallet connected');
            return;
        }
        const pool = pageData.pools.find((p) => p.pool.id === poolId);
        const formattedAmounts = Object.entries(amounts)
            .map(([coin, amount]) => `${Number(amount).toFixed(getDecimalPlaces(coin))} ${coin}`)
            .join(' and ');
        const tokenSymbols = Object.keys(amounts);
        const isAmountA = tokenSymbols.length > 1 ? tokenSymbols.indexOf(Object.keys(amounts)[0]) === 0 : undefined;
        const amount = Object.values(amounts)[0];
        const firstTokenSymbol = tokenSymbols[0];
        const poolStatic = staticData === null || staticData === void 0 ? void 0 : staticData.find((pool) => pool.pool_id === poolId);
        const firstAssetType = (_b = (_a = poolStatic === null || poolStatic === void 0 ? void 0 : poolStatic.asset_types) === null || _a === void 0 ? void 0 : _a.find((asset) => asset.asset_symbol === firstTokenSymbol)) === null || _b === void 0 ? void 0 : _b.asset_type;
        try {
            yield transactionHandler({
                poolName: (_c = pageData.pools.find((p) => p.pool.id === poolId)) === null || _c === void 0 ? void 0 : _c.pool.name,
                amount: amount,
                poolId: poolId,
                decimals: (firstAssetType && ((_d = coinInfoData === null || coinInfoData === void 0 ? void 0 : coinInfoData[firstAssetType]) === null || _d === void 0 ? void 0 : _d.decimals)) || '',
                network: network,
                action: 'withdraw',
                modalState: {
                    isOpen: true,
                    token: (pool === null || pool === void 0 ? void 0 : pool.tokens[0]) || null,
                    actionType: 'withdraw',
                },
                address: address,
                signAndExecuteTransaction: signAndExecuteTransaction,
                setNotificationInfo: setNotificationInfo,
                setIsNotificationOpen: setIsNotificationOpen,
                setIsTransactionLoading: setLoading,
                displayAmount: formattedAmounts,
                suiBalance: suiBalance,
                isAmountA: isAmountA,
                withdrawMax: poolMaxStates[poolId] || false,
                resetInputValues: () => {
                    setPoolInputValues((prev) => (Object.assign(Object.assign({}, prev), { [poolId]: Object.fromEntries((pool === null || pool === void 0 ? void 0 : pool.tokens.map((t) => [t.symbol, ''])) || []) })));
                    setPoolMaxStates((prev) => (Object.assign(Object.assign({}, prev), { [poolId]: false })));
                },
            });
        }
        catch (error) {
            console.error('Withdraw failed:', error);
        }
    });
    const handleCollectReward = () => __awaiter(void 0, void 0, void 0, function* () {
        var _a;
        if (!address) {
            console.error('No wallet connected');
            return;
        }
        const rewardAmount = pageData.portfolio.rewardAmount || '0';
        const rewardToken = 'ALPHA';
        const alphaDecimals = (_a = coinInfoData === null || coinInfoData === void 0 ? void 0 : coinInfoData['0xfe3afec26c59e874f3c1d60b8203cb3852d2bb2aa415df9548b8d688e6683f93::alpha::ALPHA']) === null || _a === void 0 ? void 0 : _a.decimals;
        try {
            yield transactionHandler({
                amount: rewardAmount,
                decimals: alphaDecimals || '9',
                network: network,
                action: 'claim',
                modalState: {
                    isOpen: true,
                    token: {
                        symbol: rewardToken,
                        name: rewardToken,
                        icon: getIcon('alpha'),
                    },
                    actionType: 'claim',
                },
                address: address,
                signAndExecuteTransaction: signAndExecuteTransaction,
                setNotificationInfo: setNotificationInfo,
                setIsNotificationOpen: setIsNotificationOpen,
                setIsTransactionLoading: setLoading,
                displayAmount: `${Number(rewardAmount).toFixed(5)} ${rewardToken}`,
                suiBalance: suiBalance,
            });
        }
        catch (error) {
            console.error('Claim failed:', error);
        }
    });
    const handleTokenInputChange = (poolId, tokenSymbol, value) => {
        const poolData = pageData.pools.find((p) => p.pool.id === poolId);
        if ((poolData === null || poolData === void 0 ? void 0 : poolData.tokens.length) === 2) {
            return;
        }
        setPoolInputValues((prev) => (Object.assign(Object.assign({}, prev), { [poolId]: value.trim() === ''
                ? Object.fromEntries((poolData === null || poolData === void 0 ? void 0 : poolData.tokens.map((t) => [t.symbol, ''])) || [])
                : Object.assign(Object.assign({}, prev[poolId]), { [tokenSymbol]: value }) })));
    };
    const handleMaxClick = (poolId, tokenSymbol) => {
        var _a;
        setPoolMaxStates((prev) => (Object.assign(Object.assign({}, prev), { [poolId]: true })));
        const poolData = pageData.pools.find((p) => p.pool.id === poolId);
        const token = poolData === null || poolData === void 0 ? void 0 : poolData.tokens.find((t) => t.symbol === tokenSymbol);
        if (!(token === null || token === void 0 ? void 0 : token.walletBalance) || parseFloat(token.walletBalance) === 0) {
            return;
        }
        if (poolData && poolData.tokens.length === 2) {
            const immediateValues = {
                [tokenSymbol]: token.walletBalance,
                [((_a = poolData.tokens.find((t) => t.symbol !== tokenSymbol)) === null || _a === void 0 ? void 0 : _a.symbol) || '']: '',
            };
            setPoolInputValues((prev) => (Object.assign(Object.assign({}, prev), { [poolId]: immediateValues })));
            if (poolData.tokens.length === 2 &&
                poolTabStates &&
                (poolTabStates[poolData.pool.id] === 0 || poolTabStates[poolData.pool.id] === undefined) &&
                isZapInActive) {
                fetchZapInQuote(token.walletBalance, tokenSymbol === poolData.tokens[0].symbol, poolData.pool.id, slippage, poolData.tokens);
            }
            else {
                calculateTokenRatios(poolId, tokenSymbol, token.walletBalance, poolData.tokens);
            }
            // calculateTokenRatios(poolId, tokenSymbol, token.walletBalance, poolData.tokens);
        }
        else {
            setPoolInputValues((prev) => (Object.assign(Object.assign({}, prev), { [poolId]: Object.assign(Object.assign({}, prev[poolId]), { [tokenSymbol]: token.walletBalance }) })));
        }
    };
    const fetchParentProtocolIconArray = (parent_protocol_name) => {
        if (typeof parent_protocol_name === 'string') {
            return [getIcon(parent_protocol_name)];
        }
        const protocolIcons = parent_protocol_name.map((protocol) => getIcon(protocol.toLowerCase()));
        return protocolIcons;
    };
    const pageData = useMemo(() => {
        var _a;
        if (!Array.isArray(staticData) || staticData.length === 0) {
            return {
                portfolio: {
                    balance: '0',
                    apy: '0',
                    alphafiTvl: '0',
                    airdropReserves: '0',
                    rewardAmount: '0',
                },
                pools: [],
            };
        }
        const pools = staticData
            .filter((poolDetails) => poolDetails.pool_id)
            .map((poolDetails) => {
            var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s;
            const { pool_id, pool_name, parent_protocol_name, asset_types, pool_tags } = poolDetails;
            const poolProtocolData = (_a = protocolData === null || protocolData === void 0 ? void 0 : protocolData.pools_data) === null || _a === void 0 ? void 0 : _a[pool_id];
            const apyHoverArray = poolProtocolData === null || poolProtocolData === void 0 ? void 0 : poolProtocolData.apy_hover;
            const pool = {
                id: pool_id,
                name: pool_name,
                protocol: parent_protocol_name,
                protocolIcon: fetchParentProtocolIconArray(parent_protocol_name),
                tvl: (poolProtocolData === null || poolProtocolData === void 0 ? void 0 : poolProtocolData.tvl) || '0',
                strategyType: (_b = poolDetails.strategy_type_headers) === null || _b === void 0 ? void 0 : _b[0],
                apy: parseFloat((poolProtocolData === null || poolProtocolData === void 0 ? void 0 : poolProtocolData.apy) || '0') > 10000
                    ? '>10K'
                    : convertToKAndM((poolProtocolData === null || poolProtocolData === void 0 ? void 0 : poolProtocolData.apy) || '0'),
                yourDeposit: ((_d = (_c = Portfolio === null || Portfolio === void 0 ? void 0 : Portfolio.pool_balances) === null || _c === void 0 ? void 0 : _c[pool_id]) === null || _d === void 0 ? void 0 : _d.balance_usd) || '0',
                currentPrice: (() => {
                    var _a;
                    const poolData = protocolData === null || protocolData === void 0 ? void 0 : protocolData.pools_data[pool_id];
                    if (poolData &&
                        'position_range' in poolData &&
                        poolData.position_range &&
                        ((_a = poolDetails.strategy_type_headers) === null || _a === void 0 ? void 0 : _a[0]) !== 'Leveraged Yield Farming') {
                        const { current_price } = poolData.position_range;
                        const price = poolDetails.is_reversed
                            ? 1 / parseFloat(current_price)
                            : parseFloat(current_price);
                        const assetSymbols = asset_types === null || asset_types === void 0 ? void 0 : asset_types.map((asset) => asset.asset_symbol);
                        if (assetSymbols && assetSymbols.length >= 2) {
                            return `${price.toFixed(5)} ${poolDetails.is_reversed ? assetSymbols[0] : assetSymbols[1]} per ${poolDetails.is_reversed ? assetSymbols[1] : assetSymbols[0]}`;
                        }
                        return `${price.toFixed(5)}`;
                    }
                    return undefined;
                })(),
                positionRange: (() => {
                    const poolData = protocolData === null || protocolData === void 0 ? void 0 : protocolData.pools_data[pool_id];
                    if (poolData && 'position_range' in poolData && poolData.position_range) {
                        const { lower, upper } = poolData.position_range;
                        return `${parseFloat(lower).toFixed(5)} - ${parseFloat(upper).toFixed(5)}`;
                    }
                    return undefined;
                })(),
                rangeStatus: (() => {
                    const poolData = protocolData === null || protocolData === void 0 ? void 0 : protocolData.pools_data[pool_id];
                    if (poolData && 'position_range' in poolData && poolData.position_range) {
                        const { lower, upper, current_price } = poolData.position_range;
                        return Number(lower) <= Number(current_price) &&
                            Number(current_price) <= Number(upper)
                            ? 'inRange'
                            : 'outOfRange';
                    }
                    return undefined;
                })(),
                tab: (asset_types === null || asset_types === void 0 ? void 0 : asset_types.map((asset) => asset.asset_symbol)) || [],
                isActive: poolDetails.is_active,
                navTabs: [
                    { label: 'Deposit', disabled: false },
                    { label: 'Withdraw', disabled: false },
                ],
            };
            if (pool_name === 'ALPHA') {
                pool.navTabs = [
                    { label: 'Deposit', disabled: false },
                    { label: 'Withdraw', disabled: false, navigateTo: '/airdrop', isNavigation: true },
                    {
                        label: 'Locked',
                        disabled: false,
                        navigateTo: '/airdrop',
                        isNavigation: true,
                    },
                ];
            }
            return {
                pool,
                tokens: (asset_types === null || asset_types === void 0 ? void 0 : asset_types.map((asset) => {
                    var _a, _b;
                    return ({
                        symbol: asset.asset_symbol,
                        name: asset.asset_symbol,
                        icon: getIcon(asset.asset_symbol.toLowerCase(), asset.asset_symbol),
                        walletBalance: poolTabStates[pool_id] === 0 || !(pool_id in poolTabStates)
                            ? (parseFloat((walletBalancesMap === null || walletBalancesMap === void 0 ? void 0 : walletBalancesMap.get(asset.asset_type)) || '0') /
                                Math.pow(10, asset.asset_decimals)).toString()
                            : (() => {
                                var _a;
                                const poolBalance = (_a = Portfolio === null || Portfolio === void 0 ? void 0 : Portfolio.pool_balances) === null || _a === void 0 ? void 0 : _a[pool_id];
                                if (!poolBalance)
                                    return '0';
                                if ('balances' in poolBalance && poolBalance.balances) {
                                    const tokenBalance = poolBalance.balances.find((balance) => balance.label.toUpperCase() === asset.asset_symbol.toUpperCase());
                                    if (!tokenBalance && asset.asset_symbol.toUpperCase() === 'ALPHA') {
                                        const lockedBalance = poolBalance.balances.find((b) => b.label === 'alpha_locked');
                                        const unlockedBalance = poolBalance.balances.find((b) => b.label === 'alpha_unlocked');
                                        if (lockedBalance || unlockedBalance) {
                                            const locked = parseFloat((lockedBalance === null || lockedBalance === void 0 ? void 0 : lockedBalance.value) || '0');
                                            const unlockedRaw = parseFloat((unlockedBalance === null || unlockedBalance === void 0 ? void 0 : unlockedBalance.value) || '0');
                                            const unlocked = unlockedRaw / Math.pow(10, asset.asset_decimals);
                                            return (locked + unlocked).toString();
                                        }
                                    }
                                    return (tokenBalance === null || tokenBalance === void 0 ? void 0 : tokenBalance.value) || '0';
                                }
                                return '0';
                            })(),
                        price: ((_a = coinInfoData === null || coinInfoData === void 0 ? void 0 : coinInfoData[asset.asset_type]) === null || _a === void 0 ? void 0 : _a.price) || '',
                        value: ((_b = poolInputValues[pool_id]) === null || _b === void 0 ? void 0 : _b[asset.asset_symbol]) || '',
                    });
                })) || [],
                tokensInPoolForTransaction: (asset_types === null || asset_types === void 0 ? void 0 : asset_types.map((asset) => {
                    var _a, _b;
                    return ({
                        symbol: asset.asset_symbol,
                        name: asset.asset_symbol,
                        icon: getIcon(asset.asset_symbol.toLowerCase(), asset.asset_symbol),
                        walletBalance: poolTabStates[pool_id] === 0 || !(pool_id in poolTabStates)
                            ? (parseFloat((walletBalancesMap === null || walletBalancesMap === void 0 ? void 0 : walletBalancesMap.get(asset.asset_type)) || '0') /
                                Math.pow(10, asset.asset_decimals)).toString()
                            : (() => {
                                var _a;
                                const poolBalance = (_a = Portfolio === null || Portfolio === void 0 ? void 0 : Portfolio.pool_balances) === null || _a === void 0 ? void 0 : _a[pool_id];
                                if (!poolBalance)
                                    return '0';
                                if ('balances' in poolBalance && poolBalance.balances) {
                                    const tokenBalance = poolBalance.balances.find((balance) => balance.label.toUpperCase() === asset.asset_symbol.toUpperCase());
                                    if (!tokenBalance && asset.asset_symbol.toUpperCase() === 'ALPHA') {
                                        const lockedBalance = poolBalance.balances.find((b) => b.label === 'alpha_locked');
                                        const unlockedBalance = poolBalance.balances.find((b) => b.label === 'alpha_unlocked');
                                        if (lockedBalance || unlockedBalance) {
                                            const locked = parseFloat((lockedBalance === null || lockedBalance === void 0 ? void 0 : lockedBalance.value) || '0');
                                            const unlockedRaw = parseFloat((unlockedBalance === null || unlockedBalance === void 0 ? void 0 : unlockedBalance.value) || '0');
                                            const unlocked = unlockedRaw / Math.pow(10, asset.asset_decimals);
                                            return (locked + unlocked).toString();
                                        }
                                    }
                                    return (tokenBalance === null || tokenBalance === void 0 ? void 0 : tokenBalance.value) || '0';
                                }
                                return '0';
                            })(),
                        price: ((_a = coinInfoData === null || coinInfoData === void 0 ? void 0 : coinInfoData[asset.asset_type]) === null || _a === void 0 ? void 0 : _a.price) || '',
                        value: ((_b = poolInputValues[pool_id]) === null || _b === void 0 ? void 0 : _b[asset.asset_symbol]) || '',
                    });
                })) || [],
                tags: (pool_tags === null || pool_tags === void 0 ? void 0 : pool_tags.map((tag) => ({
                    name: tag,
                    color: tag === 'LYF' && themeName === 'dark'
                        ? '#FFFFFF'
                        : tag === 'LYF' && themeName !== 'dark'
                            ? '#FFFFFF'
                            : '#cbe6ff',
                    style: tag === 'LYF' && themeName === 'dark'
                        ? {
                            color: '#FFFFFF',
                            border: '1px solid #FFFFFF',
                            fontFamily: 'InterSemiBold',
                            borderRadius: '30px',
                            paddingTop: '3px',
                            paddingBottom: '3px',
                        }
                        : tag === 'LYF' && themeName !== 'dark'
                            ? {
                                color: '#000',
                                border: '1px solid #293036',
                                fontFamily: 'InterSemiBold',
                                borderRadius: '30px',
                                paddingTop: '3px',
                                paddingBottom: '3px',
                            }
                            : undefined,
                }))) || [],
                tooltip: apyHoverArray
                    ? {
                        items: apyHoverArray.map((apyItem) => ({
                            label: apyItem.label,
                            value: `${parseFloat(apyItem.value).toFixed(2)}%`,
                        })),
                        infotext: `Compounding ${parent_protocol_name} Protocol APR Multiple Times a Day`,
                    }
                    : undefined,
                aboutStrategy: {
                    strategyParagraphs: {
                        paragraph1: ((_f = (_e = poolDetails.strategy_details) === null || _e === void 0 ? void 0 : _e.paragraphs) === null || _f === void 0 ? void 0 : _f[0]) || '',
                        paragraph2: ((_h = (_g = poolDetails.strategy_details) === null || _g === void 0 ? void 0 : _g.paragraphs) === null || _h === void 0 ? void 0 : _h[1]) || '',
                        paragraph3: ((_k = (_j = poolDetails.strategy_details) === null || _j === void 0 ? void 0 : _j.paragraphs) === null || _k === void 0 ? void 0 : _k[2]) || '',
                        paragraph4: ((_m = (_l = poolDetails.strategy_details) === null || _l === void 0 ? void 0 : _l.paragraphs) === null || _m === void 0 ? void 0 : _m[3]) || '',
                        paragraph5: ((_p = (_o = poolDetails.strategy_details) === null || _o === void 0 ? void 0 : _o.paragraphs) === null || _p === void 0 ? void 0 : _p[4]) || '',
                        paragraph6: ((_r = (_q = poolDetails.strategy_details) === null || _q === void 0 ? void 0 : _q.paragraphs) === null || _r === void 0 ? void 0 : _r[5]) || '',
                    },
                    performanceFee: `${((_s = poolProtocolData === null || poolProtocolData === void 0 ? void 0 : poolProtocolData.about_strategy_labels) === null || _s === void 0 ? void 0 : _s.performance_fee) || '0'}% of the Yield`,
                    rightArrow: getIcon('rightArrowWhite'),
                },
                isLoading: loading,
            };
        });
        return {
            portfolio: {
                balance: (Portfolio === null || Portfolio === void 0 ? void 0 : Portfolio.net_worth) || '0',
                rewardAmount: (Portfolio === null || Portfolio === void 0 ? void 0 : Portfolio.alpha_rewards_to_claim) || '0',
                apy: (Portfolio === null || Portfolio === void 0 ? void 0 : Portfolio.aggregated_apy) || '0',
                totalTVL: parseCurrencyString((_a = protocolData === null || protocolData === void 0 ? void 0 : protocolData.tvl) === null || _a === void 0 ? void 0 : _a.total_tvl),
                airdropReserves: parseCurrencyString(protocolData === null || protocolData === void 0 ? void 0 : protocolData.airdrop_reserves),
            },
            pools,
        };
    }, [
        staticData,
        loading,
        protocolData,
        walletBalancesMap,
        Portfolio,
        poolTabStates,
        poolInputValues,
        coinInfoData,
        themeName,
    ]);
    const handleSort = (key) => {
        setSortConfig((prevConfig) => handleSortTransition(prevConfig, key));
    };
    const handleTabClick = (tab) => {
        setActiveTab(tab);
        if (tab === 'All') {
            setSearchParams({});
        }
        else {
            setSearchParams({ tab: tab.toLowerCase() });
        }
    };
    const handlePoolTabChange = (poolId, tabIndex) => {
        var _a;
        setPoolTabStates((prev) => (Object.assign(Object.assign({}, prev), { [poolId]: tabIndex })));
        setPoolInputValues((prev) => (Object.assign(Object.assign({}, prev), { [poolId]: {} })));
        setPoolMaxStates((prev) => (Object.assign(Object.assign({}, prev), { [poolId]: false })));
        setZapInQuoteDetails({
            zapInLoader: true,
            zapInQuoteLoader: false,
            poolId: ((_a = pageData.pools.find((p) => p.pool.id === poolId)) === null || _a === void 0 ? void 0 : _a.pool.strategyType) ===
                'Leveraged Yield Farming'
                ? ''
                : poolId,
            poolDetailsPage: false,
        });
    };
    const handlePoolExpansionChange = (poolId, isExpanded) => {
        setPoolExpansionStates((prev) => (Object.assign(Object.assign({}, prev), { [poolId]: isExpanded })));
        if (!isExpanded) {
            setPoolInputValues((prev) => (Object.assign(Object.assign({}, prev), { [poolId]: {} })));
        }
    };
    const fetchProtocolNameForSearch = (protocol) => {
        return protocol.map((protocol) => protocol.toLowerCase()).join('-');
    };
    const filteredPools = useMemo(() => {
        let filtered = pageData.pools;
        // Hide specific pool
        filtered = filtered.filter((poolData) => poolData.pool.id !== '0x6ee8f60226edf48772f81e5986994745dae249c2605a5b12de6602ef1b05b0c1');
        if (activeTab === 'All') {
            filtered = filtered.filter((poolData) => {
                const pool = poolData.pool;
                return pool.isActive;
            });
        }
        else if (activeTab === 'Retired') {
            filtered = filtered.filter((poolData) => {
                const pool = poolData.pool;
                return !pool.isActive;
            });
        }
        else if (activeTab === 'Stables') {
            filtered = filtered.filter((poolData) => {
                const pool = poolData.pool;
                return (pool.isActive &&
                    (pool.tab.includes('USDC') || pool.tab.includes('USDT') || pool.tab.includes('suiUSDT')));
            });
        }
        else {
            filtered = filtered.filter((poolData) => {
                const pool = poolData.pool;
                if (activeTab === 'Holdings') {
                    return parseFloat(pool.yourDeposit) > 0;
                }
                return pool.isActive && pool.tab.includes(activeTab);
            });
        }
        if (searchTerm.trim()) {
            filtered = filtered.filter((poolData) => {
                var _a, _b;
                const pool = poolData.pool;
                const name = (_a = pool.name) === null || _a === void 0 ? void 0 : _a.toLowerCase();
                const searchLower = searchTerm.toLowerCase();
                const protocol = fetchProtocolNameForSearch(pool.protocol);
                const tag = (_b = poolData === null || poolData === void 0 ? void 0 : poolData.tags) === null || _b === void 0 ? void 0 : _b.map((tag) => tag.name.toLowerCase());
                return ((name === null || name === void 0 ? void 0 : name.includes(searchLower)) ||
                    protocol.includes(searchLower) ||
                    (tag && Array.isArray(tag) && tag.includes(searchLower)));
            });
        }
        return filtered;
    }, [pageData.pools, activeTab, searchTerm]);
    const sortedPools = useMemo(() => {
        return sortPools(filteredPools, sortConfig);
    }, [filteredPools, sortConfig]);
    const fetchZapInQuote = (depositAmount, isInputA, pool, slippage, tokens) => __awaiter(void 0, void 0, void 0, function* () {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l;
        try {
            setZapInQuoteDetails({
                zapInLoader: true,
                zapInQuoteLoader: true,
                poolId: ((_a = pageData.pools.find((p) => p.pool.id === pool)) === null || _a === void 0 ? void 0 : _a.pool.strategyType) ===
                    'Leveraged Yield Farming'
                    ? ''
                    : pool,
                poolDetailsPage: false,
            });
            const poolDetails = staticData === null || staticData === void 0 ? void 0 : staticData.find((pooldata) => pooldata.pool_id === pool);
            const tokenDetails = isInputA ? (_b = poolDetails === null || poolDetails === void 0 ? void 0 : poolDetails.asset_types) === null || _b === void 0 ? void 0 : _b[0] : (_c = poolDetails === null || poolDetails === void 0 ? void 0 : poolDetails.asset_types) === null || _c === void 0 ? void 0 : _c[1];
            const otherTokenDetails = isInputA
                ? (_d = poolDetails === null || poolDetails === void 0 ? void 0 : poolDetails.asset_types) === null || _d === void 0 ? void 0 : _d[1]
                : (_e = poolDetails === null || poolDetails === void 0 ? void 0 : poolDetails.asset_types) === null || _e === void 0 ? void 0 : _e[0];
            if ((tokenDetails === null || tokenDetails === void 0 ? void 0 : tokenDetails.asset_type) && (otherTokenDetails === null || otherTokenDetails === void 0 ? void 0 : otherTokenDetails.asset_type)) {
                const tokenBalance = (parseFloat((walletBalancesMap === null || walletBalancesMap === void 0 ? void 0 : walletBalancesMap.get(tokenDetails === null || tokenDetails === void 0 ? void 0 : tokenDetails.asset_type)) || '0') /
                    Math.pow(10, (tokenDetails === null || tokenDetails === void 0 ? void 0 : tokenDetails.asset_decimals)))
                    .toFixed(tokenDetails === null || tokenDetails === void 0 ? void 0 : tokenDetails.asset_decimals)
                    .toString();
                if (tokenBalance && parseFloat(tokenBalance) >= parseFloat(depositAmount)) {
                    //fetch get quote from sdk
                    const depositAmountNormalized = (parseFloat(depositAmount) *
                        Math.pow(10, (tokenDetails === null || tokenDetails === void 0 ? void 0 : tokenDetails.asset_decimals)))
                        .toString()
                        .split('.')[0];
                    const alphafiSdk = createAlphaFiSDK(network, address || '');
                    const quote = yield alphafiSdk.zapDepositQuote({
                        poolId: (poolDetails === null || poolDetails === void 0 ? void 0 : poolDetails.pool_id) || '',
                        inputCoinAmount: BigInt(depositAmountNormalized),
                        isInputA: isInputA,
                        slippage: slippage / 100 || 0.01,
                    });
                    if (quote && poolDetails) {
                        const token1Amount = (parseFloat(quote[0]) /
                            Math.pow(10, ((_g = (_f = poolDetails === null || poolDetails === void 0 ? void 0 : poolDetails.asset_types) === null || _f === void 0 ? void 0 : _f[0]) === null || _g === void 0 ? void 0 : _g.asset_decimals)))
                            .toFixed(4)
                            .toString();
                        const token2Amount = (parseFloat(quote[1]) /
                            Math.pow(10, ((_j = (_h = poolDetails === null || poolDetails === void 0 ? void 0 : poolDetails.asset_types) === null || _h === void 0 ? void 0 : _h[1]) === null || _j === void 0 ? void 0 : _j.asset_decimals)))
                            .toFixed(4)
                            .toString();
                        const token1Price = tokens[0].price;
                        const token2Price = tokens[1].price;
                        const token1AmountInUsd = token1Price
                            ? (parseFloat(token1Amount) * parseFloat(token1Price)).toFixed(2).toString()
                            : '0';
                        const token2AmountInUsd = token2Price
                            ? (parseFloat(token2Amount) * parseFloat(token2Price)).toFixed(2).toString()
                            : '0';
                        const inputUSD = token1Price && isInputA
                            ? parseFloat(depositAmount) * parseFloat(token1Price)
                            : token2Price
                                ? parseFloat(depositAmount) * parseFloat(token2Price)
                                : 0;
                        const tokenShareInZap = token1AmountInUsd || token2AmountInUsd
                            ? [
                                ((parseFloat(token1AmountInUsd) * 100) /
                                    (parseFloat(token1AmountInUsd) + parseFloat(token2AmountInUsd)))
                                    .toFixed(2)
                                    .toString(),
                                ((parseFloat(token2AmountInUsd) * 100) /
                                    (parseFloat(token1AmountInUsd) + parseFloat(token2AmountInUsd)))
                                    .toFixed(2)
                                    .toString(),
                            ]
                            : ['0', '0'];
                        const zapSlippage = (((inputUSD - (parseFloat(token1AmountInUsd) + parseFloat(token2AmountInUsd))) * 100) /
                            inputUSD)
                            .toFixed(2)
                            .toString();
                        const swapAmountArray = isInputA
                            ? [
                                (inputUSD - parseFloat(token1AmountInUsd) / parseFloat(token1Price))
                                    .toFixed(4)
                                    .toString(),
                                token2Amount,
                            ]
                            : [
                                token1Amount,
                                (inputUSD - parseFloat(token2AmountInUsd) / parseFloat(token2Price))
                                    .toFixed(4)
                                    .toString(),
                            ];
                        setZapInQuoteDetails({
                            token1Amount,
                            token2Amount,
                            token1AmountInUsd,
                            token2AmountInUsd,
                            token1Icon: tokens[0].icon,
                            token2Icon: tokens[1].icon,
                            tokenShareInZap: tokenShareInZap,
                            zapSlippage,
                            token1Price,
                            token2Price,
                            token1Symbol: tokens[0].symbol,
                            token2Symbol: tokens[1].symbol,
                            isInputA,
                            swapAmountArray,
                            depositAmount: depositAmountNormalized.toString(),
                            depositAmountWithoutExponent: depositAmount.toString(),
                            reverseIcon: themeName === 'dark'
                                ? getIcon('reverseTokensIconDarkTheme')
                                : getIcon('reverseTokensIcon'),
                            slippageIcon: themeName === 'dark' ? getIcon('whiteSlippageIcon') : getIcon('zapSlippageIcon'),
                            zapInIcon: themeName === 'dark' && isZapInActive
                                ? getIcon('zapInIcon')
                                : themeName === 'dark' && !isZapInActive
                                    ? getIcon('zapInIconDarkTheme')
                                    : themeName !== 'dark' && isZapInActive
                                        ? getIcon('zapInIconDarkTheme')
                                        : themeName !== 'dark' && !isZapInActive
                                            ? getIcon('zapInIcon')
                                            : '',
                            depositsIcon: themeName === 'dark'
                                ? getIcon('depositsIconForDarkTheme')
                                : getIcon('depositsIcon'),
                            zapInLoader: false,
                            zapInQuoteLoader: false,
                            poolId: ((_k = pageData.pools.find((p) => p.pool.id === (poolDetails === null || poolDetails === void 0 ? void 0 : poolDetails.pool_id))) === null || _k === void 0 ? void 0 : _k.pool.strategyType) === 'Leveraged Yield Farming'
                                ? ''
                                : poolDetails === null || poolDetails === void 0 ? void 0 : poolDetails.pool_id,
                            activeToken: isInputA ? tokens[0].symbol : tokens[1].symbol,
                            customZapSlippagePopupStyle: 'absolute left-[27%] mt-[25px] z-50',
                            poolDetailsPage: false,
                        });
                    }
                }
                else {
                    setZapInQuoteDetails({
                        zapInLoader: true,
                        zapInQuoteLoader: false,
                        poolId: ((_l = pageData.pools.find((p) => p.pool.id === (poolDetails === null || poolDetails === void 0 ? void 0 : poolDetails.pool_id))) === null || _l === void 0 ? void 0 : _l.pool.strategyType) ===
                            'Leveraged Yield Farming'
                            ? ''
                            : poolDetails === null || poolDetails === void 0 ? void 0 : poolDetails.pool_id,
                        activeToken: isInputA ? tokens[0].symbol : tokens[1].symbol,
                        poolDetailsPage: false,
                    });
                }
            }
        }
        catch (error) {
            console.log('error in fetching zapin quote', error);
        }
    });
    if (isSystemLoading)
        return (_jsx("div", { className: "flex items-center justify-center h-screen mt-[-4vw]", children: _jsx(LoaderComp, { dimensions: "w-[9vw] md:w-[3vw] h-[9vw] md:h-[3vw]", borderWidth: "border-[0.5vw]" }) }));
    return (_jsxs("div", { className: "theme-bg-alphafi-main-body min-h-screen pb-[150px] xl:pb-[267px] -mt-[1px]", children: [_jsx(DashboardStats, { isWalletConnected: isWalletConnected, userStats: [
                    {
                        value: `$${convertToKAndM(pageData.portfolio.balance)}`,
                        label: 'Balance',
                    },
                    {
                        value: `${convertToKAndM(pageData.portfolio.apy)}%`,
                        label: 'APY',
                    },
                    {
                        value: convertToKAndM((_a = pageData.portfolio) === null || _a === void 0 ? void 0 : _a.rewardAmount),
                        label: 'ALPHA',
                        collectButton: true,
                        handleCollectButtonClick: handleCollectReward,
                        alphaIcon: getIcon('alpha'),
                    },
                ], protocolStats: [
                    {
                        value: `$${convertToKAndM(pageData.portfolio.totalTVL || '0')}`,
                        label: 'TVL',
                        info: (protocolData === null || protocolData === void 0 ? void 0 : protocolData.tvl)
                            ? {
                                icon: getIcon('infoIcon'),
                                tooltip: {
                                    items: [
                                        {
                                            label: 'AlphaFi TVL',
                                            value: `$${convertToKAndM(parseCurrencyString(protocolData.tvl.alphafi_tvl))}`,
                                        },
                                        {
                                            label: 'AlphaLend TVL',
                                            value: `$${convertToKAndM(parseCurrencyString(protocolData.tvl.alphalend_tvl))}`,
                                        },
                                        {
                                            label: 'stSUI TVL',
                                            value: `$${convertToKAndM(parseCurrencyString(protocolData.tvl.stsui_tvl))}`,
                                        },
                                        {
                                            label: 'TVL',
                                            value: `$${convertToKAndM(parseCurrencyString(protocolData.tvl.total_tvl))}`,
                                        },
                                    ],
                                    isPointerVisible: false,
                                },
                            }
                            : undefined,
                    },
                    {
                        value: `$${convertToKAndM(pageData.portfolio.airdropReserves)}`,
                        label: 'Airdrop Reserves',
                    },
                ], defaultStats: {
                    title: 'Deposit and Earn. Real Yields. Fully Automated',
                    stats: [
                        {
                            value: `$${convertToKAndM(pageData.portfolio.totalTVL || '0')}`,
                            label: 'TVL',
                            info: (protocolData === null || protocolData === void 0 ? void 0 : protocolData.tvl)
                                ? {
                                    icon: getIcon('infoIcon'),
                                    tooltip: {
                                        items: [
                                            {
                                                label: 'AlphaFi TVL',
                                                value: `$${convertToKAndM(parseCurrencyString(protocolData.tvl.alphafi_tvl))}`,
                                            },
                                            {
                                                label: 'AlphaLend TVL',
                                                value: `$${convertToKAndM(parseCurrencyString(protocolData.tvl.alphalend_tvl))}`,
                                            },
                                            {
                                                label: 'stSUI TVL',
                                                value: `$${convertToKAndM(parseCurrencyString(protocolData.tvl.stsui_tvl))}`,
                                            },
                                            {
                                                label: 'TVL',
                                                value: `$${convertToKAndM(parseCurrencyString(protocolData.tvl.total_tvl))}`,
                                            },
                                        ],
                                        isPointerVisible: false,
                                    },
                                }
                                : undefined,
                        },
                        {
                            value: `$${convertToKAndM(pageData.portfolio.airdropReserves)}`,
                            label: 'Airdrop Reserves',
                        },
                    ],
                    walletConnectFunctionality: walletConnectFunctionality,
                    bgImage: themeName === 'dark' && isMobile
                        ? getImage('dashboardDarkMobileBg')
                        : themeName !== 'dark' && isMobile
                            ? getImage('dashboardLightMobileBg')
                            : themeName === 'dark' && !isMobile
                                ? getImage('dashboardDarkBg')
                                : getImage('dashboardLightBg'),
                } }), _jsxs("div", { className: "flex lg:flex-row lg:max-w-[985px] xl:max-w-[1390px] w-full flex-col-reverse justify-center items-center md:justify-between mx-auto px-5 md:px-[unset]", children: [_jsx(FilterTabV1, { tabs: AVAILABLE_TABS, activeTab: activeTab, handleTabClick: handleTabClick }), _jsx("div", { className: "flex justify-end w-full md:px-[unset] mb-[26px] md:mb-7 lg:mb-[unset] md:mr-[34px] lg:mr-[unset] mr-[unset]", children: _jsx(SearchInputV1, { search: searchTerm, handleSearchChange: (e) => setSearchTerm(e.target.value), clearSearch: () => setSearchTerm(''), clearSearchIcon: getIcon('clearSearchIcon'), searchIcon: themeName === 'dark'
                                ? getIcon('darkThemeSearchIcon')
                                : getIcon('defaultThemeSearchIcon') }) })] }), _jsx("div", { className: "w-full flex justify-center mx-auto mt-4 md:mt-11", children: _jsx(RowHeaderV1, { sortConfig: sortConfig || undefined, onSort: (key) => handleSort(key), columns: [
                        { key: 'assets', label: 'Assets', align: 'left', showOnMobile: true },
                        { key: 'protocols', label: 'Protocols', align: 'center', showOnMobile: true },
                        { key: 'apy', label: 'APY', align: 'center', showOnMobile: true },
                        { key: 'tvl', label: 'TVL', align: 'center', showOnMobile: false },
                        { key: 'your_deposit', label: 'Your Deposit', align: 'center', showOnMobile: true },
                    ], sortUpIcon: themeName === 'dark' ? getIcon('darkThemeUpArrow') : getIcon('upArrowBlack'), sortDownIcon: themeName === 'dark' ? getIcon('darkThemeDownArrow') : getIcon('downArrowBlack'), sortDefaultIcon: themeName === 'dark' ? getIcon('darkThemeDefaultArrow') : getIcon('defaultArrow') }) }), _jsx("div", { className: "flex flex-col w-full justify-center items-center space-y-3.5 xl:space-y-5 md:py-3 lg:py-4 px-4 xl:px-0", children: sortedPools.map((poolData) => (_jsx(RowV1, { pool: poolData.pool, tokens: poolData.tokens, navTabs: poolData.pool.navTabs, tags: poolData.tags, tooltip: poolData.tooltip, aboutStrategy: poolData.aboutStrategy, isLoading: loading, activeTabIndex: poolTabStates[poolData.pool.id] || 0, onTabChange: (tabIndex) => handlePoolTabChange(poolData.pool.id, tabIndex), isExpanded: poolExpansionStates[poolData.pool.id] || false, onExpansionChange: (isExpanded) => handlePoolExpansionChange(poolData.pool.id, isExpanded), onDeposit: (amounts) => handleDeposit(poolData.pool.id, amounts), onWithdraw: (amounts) => handleWithdraw(poolData.pool.id, amounts), onZapInAndDeposit: () => handleZapInAndDeposit(poolData.pool.id), onMaxClick: (tokenSymbol) => handleMaxClick(poolData.pool.id, tokenSymbol), onTokenValueChange: (tokenSymbol, value) => {
                        var _a, _b;
                        setPoolMaxStates((prev) => (Object.assign(Object.assign({}, prev), { [poolData.pool.id]: false })));
                        const currentPool = pageData.pools.find((p) => p.pool.id === poolData.pool.id);
                        if (!currentPool || currentPool.tokens.length !== 2) {
                            return { [tokenSymbol]: value };
                        }
                        if (value.trim() === '') {
                            const clearedValues = Object.fromEntries(currentPool.tokens.map((t) => [t.symbol, '']));
                            setPoolInputValues((prev) => (Object.assign(Object.assign({}, prev), { [poolData.pool.id]: clearedValues })));
                            setZapInQuoteDetails((prev) => {
                                var _a;
                                return (Object.assign(Object.assign({}, prev), { zapInLoader: true, zapInQuoteLoader: false, poolId: ((_a = pageData.pools.find((p) => p.pool.id === poolData.pool.id)) === null || _a === void 0 ? void 0 : _a.pool.strategyType) === 'Leveraged Yield Farming'
                                        ? ''
                                        : poolData.pool.id, poolDetailsPage: false, zapSlippage: '' }));
                            });
                            return clearedValues;
                        }
                        const immediateResult = {
                            [tokenSymbol]: value,
                            [((_a = currentPool.tokens.find((t) => t.symbol !== tokenSymbol)) === null || _a === void 0 ? void 0 : _a.symbol) || '']: '',
                        };
                        setPoolInputValues((prev) => {
                            var _a;
                            return (Object.assign(Object.assign({}, prev), { [poolData.pool.id]: Object.assign(Object.assign({}, prev[poolData.pool.id]), { [tokenSymbol]: value, [((_a = currentPool.tokens.find((t) => t.symbol !== tokenSymbol)) === null || _a === void 0 ? void 0 : _a.symbol) || '']: '' }) }));
                        });
                        if (currentPool.tokens.length === 2 &&
                            poolTabStates &&
                            (poolTabStates[poolData.pool.id] === 0 ||
                                poolTabStates[poolData.pool.id] === undefined) &&
                            isZapInActive &&
                            parseFloat(value) === 0) {
                            setZapInQuoteDetails({
                                zapInLoader: true,
                                zapInQuoteLoader: false,
                                poolId: ((_b = pageData.pools.find((p) => p.pool.id === poolData.pool.id)) === null || _b === void 0 ? void 0 : _b.pool.strategyType) === 'Leveraged Yield Farming'
                                    ? ''
                                    : poolData.pool.id, //poolData.pool.id,
                                activeToken: tokenSymbol,
                                poolDetailsPage: false,
                            });
                        }
                        if (currentPool.tokens.length === 2 &&
                            poolTabStates &&
                            (poolTabStates[poolData.pool.id] === 0 ||
                                poolTabStates[poolData.pool.id] === undefined) &&
                            isZapInActive &&
                            parseFloat(value) > 0) {
                            fetchZapInQuote(value, tokenSymbol === currentPool.tokens[0].symbol, poolData.pool.id, slippage, currentPool.tokens);
                        }
                        else {
                            calculateTokenRatios(poolData.pool.id, tokenSymbol, value, currentPool.tokens);
                        }
                        return immediateResult;
                    }, onTokenInputChange: (tokenSymbol, value) => handleTokenInputChange(poolData.pool.id, tokenSymbol, value), poolRedirectHandler: () => {
                        navigate(`/pool-details/${poolData.pool.name}?id=${poolData.pool.id}`);
                    }, balanceText: poolTabStates[poolData.pool.id] === 0 || !(poolData.pool.id in poolTabStates)
                        ? 'In Wallet:'
                        : 'In Vault:', isButtonDisabled: (() => {
                        const inputValues = poolInputValues[poolData.pool.id];
                        const tabIndex = poolTabStates[poolData.pool.id];
                        const maxClicked = poolMaxStates[poolData.pool.id] || false;
                        const rangeStatus = poolData.pool.rangeStatus;
                        const buttonState = getButtonState(inputValues, tabIndex, poolData.tokens, maxClicked, rangeStatus);
                        return buttonState.disabled;
                    })(), buttonText: (() => {
                        const inputValues = poolInputValues[poolData.pool.id];
                        const tabIndex = poolTabStates[poolData.pool.id];
                        const maxClicked = poolMaxStates[poolData.pool.id] || false;
                        const buttonState = getButtonState(inputValues, tabIndex, poolData.tokens, maxClicked);
                        return buttonState.text;
                    })(), walletConnectFunctionality: isWalletConnected ? undefined : walletConnectFunctionality, zapinIcon: themeName === 'dark' && isZapInActive
                        ? getIcon('zapInIcon')
                        : themeName === 'dark' && !isZapInActive
                            ? getIcon('zapInIconDarkTheme')
                            : themeName !== 'dark' && isZapInActive
                                ? getIcon('zapInIconDarkTheme')
                                : themeName !== 'dark' && !isZapInActive
                                    ? getIcon('zapInIcon')
                                    : '', switchTokenIcon: themeName === 'dark'
                        ? getIcon('switchTokenDarkThemeIcon')
                        : getIcon('switchTokenIcon'), zapInQuoteDetails: zapInQuoteDetails, isZapInActive: isZapInActive, setIsZapInActive: setIsZapInActive, slippage: slippage, setSlippage: setSlippage }, poolData.pool.id))) }), _jsx(SwapButton, { isSwapModalOpen: isSwapModalOpen, onClick: () => setIsSwapModalOpen(true), themeName: themeName }), _jsx(SwapModal, { isOpen: isSwapModalOpen, onClose: () => setIsSwapModalOpen(false), onNotification: handleSwapNotification, loading: loading, setLoading: setLoading, walletConnectFunctionality: walletConnectFunctionality, isWalletConnected: isWalletConnected, theme: themeName }), _jsx(NotificationComp, { isOpen: isNotificationOpen, setIsOpen: setIsNotificationOpen, info: notificationInfo })] }));
};
export default PoolsPage;
